import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@/types'
import api, { setTokens, clearTokens, getToken } from '@/lib/api'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  emailVerified: boolean

  // Actions
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: (credential: string) => Promise<void>
  loginWithApple: (identityToken: string, user?: { email?: string }) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  setUser: (user: User) => void
  setEmailVerified: (verified: boolean) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      emailVerified: false,

      login: async (email: string, password: string) => {
        const response = await api.post('/login', { email, password })
        const { token, refresh_token, email_verified, user_id } = response.data

        setTokens(token, refresh_token)

        set({
          user: {
            id: user_id,
            email,
            email_verified,
            has_used_trial: false,
            trial_started_at: null,
            trial_platform: null,
            google_sub: null,
            apple_sub: null,
            password_set: true,
          },
          isAuthenticated: true,
          emailVerified: email_verified,
        })
      },

      loginWithGoogle: async (credential: string) => {
        const response = await api.post('/login/google', { id_token: credential })
        const { token, refresh_token, email, email_verified, user_id } =
          response.data

        setTokens(token, refresh_token)

        set({
          user: {
            id: user_id,
            email,
            email_verified,
            has_used_trial: false,
            trial_started_at: null,
            trial_platform: null,
            google_sub: credential,
            apple_sub: null,
            password_set: false,
          },
          isAuthenticated: true,
          emailVerified: email_verified,
        })
      },

      loginWithApple: async (identityToken: string, user?: { email?: string }) => {
        const response = await api.post('/login/apple', {
          identity_token: identityToken,
          user,
        })
        const { token, refresh_token, email, email_verified, user_id } =
          response.data

        setTokens(token, refresh_token)

        set({
          user: {
            id: user_id,
            email,
            email_verified,
            has_used_trial: false,
            trial_started_at: null,
            trial_platform: null,
            google_sub: null,
            apple_sub: identityToken,
            password_set: false,
          },
          isAuthenticated: true,
          emailVerified: email_verified,
        })
      },

      register: async (email: string, password: string) => {
        const response = await api.post('/register', { email, password })
        const { token, refresh_token, user_id } = response.data

        setTokens(token, refresh_token)

        set({
          user: {
            id: user_id,
            email,
            email_verified: false,
            has_used_trial: false,
            trial_started_at: null,
            trial_platform: null,
            google_sub: null,
            apple_sub: null,
            password_set: true,
          },
          isAuthenticated: true,
          emailVerified: false,
        })
      },

      logout: async () => {
        try {
          await api.post('/logout')
        } catch {
          // Ignore logout errors
        }
        clearTokens()
        set({
          user: null,
          isAuthenticated: false,
          emailVerified: false,
        })
      },

      checkAuth: async () => {
        const token = getToken()
        if (!token) {
          set({ isLoading: false, isAuthenticated: false })
          return
        }

        try {
          const response = await api.get('/me')
          const user = response.data as User

          set({
            user,
            isAuthenticated: true,
            emailVerified: user.email_verified,
            isLoading: false,
          })
        } catch {
          clearTokens()
          set({
            user: null,
            isAuthenticated: false,
            emailVerified: false,
            isLoading: false,
          })
        }
      },

      setUser: (user: User) => {
        set({ user, emailVerified: user.email_verified })
      },

      setEmailVerified: (verified: boolean) => {
        const currentUser = get().user
        if (currentUser) {
          set({
            user: { ...currentUser, email_verified: verified },
            emailVerified: verified,
          })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        emailVerified: state.emailVerified,
      }),
    }
  )
)
